import { api } from './api';
import type {
  ApiEnvelope, AuditFields, EngineListParams, PaginatedEnvelope, Party,
} from './notification-engine.types';

const BASE = '/api/v1/recipient-masters';

/**
 * One step of a resolution chain.
 *
 * Array position IS execution order — there is no ordering column, which is
 * what removes the reorder race. `inputMapping` names where each declared input
 * comes from; `outputMapping` names the aliases a later step can read.
 */
export interface FunctionAttachment {
  functionMasterId: string;
  inputMapping?: Record<string, { source: string; path?: string; value?: unknown }>;
  outputMapping?: Record<string, string>;
  timeoutSeconds?: number | null;
}

/** An action offered to whoever this recipient resolves to. */
export interface ActionAttachment {
  actionMasterId: string;
  notificationMasterId?: string | null;
  isDefault?: boolean;
}

/**
 * A recipient_master row — a named audience and how it is found.
 *
 * STATIC names its parties outright, DIRECT reads them off the trigger, and
 * DYNAMIC runs a function chain to resolve them.
 */
export interface RecipientMaster extends AuditFields {
  _id: string;
  recipientCode: string;
  description?: string | null;
  recipientType: string;            // STATIC | DIRECT | DYNAMIC
  parties?: Party[];
  functions?: FunctionAttachment[];
  actions?: ActionAttachment[];
}

export type RecipientMasterCreateRequest =
  Omit<RecipientMaster, '_id' | keyof AuditFields> & { appId?: string | null };
export type RecipientMasterUpdateRequest = Partial<RecipientMasterCreateRequest>;

export interface ChainValidationResult {
  isValid?: boolean;
  message?: string;
  [key: string]: unknown;
}

/**
 * Who a recipient rule reaches, run against sample inputs.
 *
 * Sends nothing and writes nothing — the same resolution a fire performs, stopped
 * before delivery. `parties` carries the identities a per-recipient payload is
 * keyed on, which is what makes attaching a file to ONE person possible from a
 * screen: the alternative is knowing somebody's user id by heart.
 */
export interface ResolvedParty {
  partyType?: string;
  userId?: string | null;
  personnelMasterId?: string | null;
  name?: string | null;
  contact?: { email?: string | null; phone?: string | null; whatsapp?: string | null } | null;
  [key: string]: unknown;
}

export interface ResolutionPreview {
  recipientCode?: string;
  recipientType?: string;
  outcome?: string;
  reason?: string | null;
  resolvedCount?: number;
  parties?: ResolvedParty[];
  reachable?: Record<string, number>;
  unreachable?: ResolvedParty[];
}

export const recipientMastersService = {
  list: async (params?: EngineListParams): Promise<PaginatedEnvelope<RecipientMaster>> => {
    const response = await api.get<PaginatedEnvelope<RecipientMaster>>(BASE, { params });
    return response.data;
  },
  getById: async (id: string): Promise<RecipientMaster> => {
    const response = await api.get<ApiEnvelope<RecipientMaster>>(`${BASE}/${id}`);
    return response.data.data;
  },
  create: async (data: RecipientMasterCreateRequest): Promise<RecipientMaster> => {
    const response = await api.post<ApiEnvelope<RecipientMaster>>(BASE, data);
    return response.data.data;
  },
  update: async (id: string, data: RecipientMasterUpdateRequest): Promise<RecipientMaster> => {
    const response = await api.put<ApiEnvelope<RecipientMaster>>(`${BASE}/${id}`, data);
    return response.data.data;
  },
  /** Replaces the chain wholesale — position is order, so a partial edit has no meaning. */
  setFunctions: async (id: string, functions: FunctionAttachment[]): Promise<RecipientMaster> => {
    const response = await api.put<ApiEnvelope<RecipientMaster>>(`${BASE}/${id}/functions`, { functions });
    return response.data.data;
  },
  setActions: async (id: string, actions: ActionAttachment[]): Promise<RecipientMaster> => {
    const response = await api.put<ApiEnvelope<RecipientMaster>>(`${BASE}/${id}/actions`, { actions });
    return response.data.data;
  },
  /** Who this rule reaches for these inputs. Nothing is sent; READ is enough. */
  previewResolution: async (
    id: string,
    body: { payload?: Record<string, unknown>; context?: Record<string, unknown> },
  ): Promise<ResolutionPreview> => {
    const response = await api.post<ApiEnvelope<ResolutionPreview>>(
      `${BASE}/${id}/preview-resolution`, body);
    return response.data.data;
  },
  /** Type-checks the chain without saving: no forward references, contracts agree. */
  validateChain: async (id: string): Promise<ChainValidationResult> => {
    const response = await api.post<ApiEnvelope<ChainValidationResult>>(`${BASE}/${id}/validate-chain`);
    return response.data.data;
  },
  /** Retire without deleting: the runtime reads only active rows. */
  deactivate: async (id: string): Promise<RecipientMaster> => {
    const response = await api.post<ApiEnvelope<RecipientMaster>>(`${BASE}/${id}/deactivate`);
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`${BASE}/${id}`);
    return response.data.data;
  },
};
